import { useQuery } from 'react-query';
import chunk from 'lodash/chunk';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';

import { searchClient } from 'apis/algolia';

import { getProductsQueryParams } from 'utils/algolia';
import type { IProduct } from 'apis/algolia';

const PRODUCT_LIMIT_PER_PAGE = 1;
const ALGOLIA_VENDOR_LIST_INDEX = 'vendors_list_v1';
const MAX_MULTIPLE_QUERIES = 50;

type Category = {
  id: string;
  name: string;
  alias?: string;
  subcategories: Array<{
    eligible: boolean;
    value: string;
  }>;
};

type EmployeeWallet = {
  company_wallet_configuration: {
    categories: Array<Category>;
    blacklisted_vendors: Array<string>;
  };
};

const getCategoriesAndQueries = <T extends EmployeeWallet>(
  country: string,
  employee_wallets: Array<T>
) =>
  employee_wallets.flatMap((employee_wallet) =>
    employee_wallet.company_wallet_configuration.categories.map(
      (category: Category) => {
        const { facet_filters, numeric_filters } = getProductsQueryParams({
          country,
          employee_wallet,
          subcategories: category.subcategories
            .map(({ value }) => value)
            .filter((sc) => sc),
        });
        return {
          category,
          query: {
            indexName: ALGOLIA_VENDOR_LIST_INDEX,
            query: '',
            params: {
              hitsPerPage: PRODUCT_LIMIT_PER_PAGE,
              facetFilters: facet_filters,
              numericFilters: numeric_filters,
            },
            attributesToRetrieve: ['id'],
          },
        };
      }
    )
  );

type Query = ReturnType<typeof getCategoriesAndQueries>[0]['query'][];

const processCategoryQueries = async (queries: Query) => {
  const chunked_queries = chunk(queries, MAX_MULTIPLE_QUERIES);
  const chunked_query_promises = chunked_queries.map((queries_chunk) =>
    searchClient.multipleQueries<IProduct>(queries_chunk)
  );
  const chunked_results = await Promise.all(chunked_query_promises);
  return chunked_results.flatMap((chunked_result) => chunked_result.results);
};

const getCategoriesWithQueryResults = (
  categories: Array<Category>,
  results: Awaited<ReturnType<typeof processCategoryQueries>>
) => {
  const cats_with_results = categories.filter((_c, i) => results[i].nbHits);
  // TODO: Why is this filtering on both `name` and `id`?
  const cats_uniq_by_id = uniqBy(cats_with_results, (c) => c.id.toLowerCase());
  const cats_uniq = uniqBy(cats_uniq_by_id, (c) => c.name.toLowerCase());
  return sortBy(cats_uniq, (c) => c.alias);
};

const getCategoriesWithProducts = async <T extends EmployeeWallet>({
  employee_wallets,
  country,
}: {
  employee_wallets: Array<T>;
  country: string;
}) => {
  const categories_and_queries = getCategoriesAndQueries(
    country,
    employee_wallets
  );
  const categories = categories_and_queries.map(({ category }) => category);
  const queries = categories_and_queries.map(({ query }) => query);
  const results = await processCategoryQueries(queries);
  return getCategoriesWithQueryResults(categories, results);
};

type UseQueryCategoriesWithProductsArgs = {
  country: string;
  employee_wallets: Array<EmployeeWallet>;
  queryKey?: Array<string | Record<string, string>>;
  onError: (error: Error) => void;
};

const useQueryCategoriesWithProducts = ({
  country,
  employee_wallets,
  queryKey = ['getCategoriesWithProducts'],
  onError,
}: UseQueryCategoriesWithProductsArgs) => {
  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      getCategoriesWithProducts({
        employee_wallets,
        country,
      }),
    { onError }
  );
  return {
    data,
    isLoading,
  };
};

export default useQueryCategoriesWithProducts;
